import {
    AgentsActions,
    IActionQueuesPayload,
    IActionEditDefintionQueuesPayload,
    IUpdateAgentQueuePayload } from "DistributedTaskControls/Actions/AgentsActions";
import { ActionsHubManager } from "DistributedTaskControls/Common/Actions/ActionsHubManager";
import { StoreKeys, AgentsStoreChangedEvents } from "DistributedTaskControls/Common/Common";
import * as StoreCommonBase from "DistributedTaskControls/Common/Stores/Base";
import { UrlUtilities } from "DistributedTaskControls/Common/UrlUtilities";
import * as Types from "DistributedTaskControls/Phase/Types";
import * as Resources from "DistributedTaskControls/Resources/TFS.Resources.DistributedTaskControls";

import * as TaskUtils from "DistributedTasksCommon/TFS.Tasks.Utils";

import { TaskAgentQueue, TaskAgentPoolReference } from "TFS/DistributedTask/Contracts";

import * as VssContext from "VSS/Context";
import { FeatureAvailabilityService } from "VSS/FeatureAvailability/Services";
import * as Utils_Array from "VSS/Utils/Array";
import * as Utils_String from "VSS/Utils/String";

export interface IAgentsState {
    defaultQueueId: number;
    queues: TaskAgentQueue[];
    agentSpecifications: Types.IAgentSpecification[];
    selectedAgentSpecification?: Types.IAgentSpecification;
    agentSpecificationErrorMessage: string;
    isLoadingAgentSpecifications: boolean;
}

export interface IAgentsStoreArgs {
    defaultQueueId?: number;
    agentSpecification?: Types.IAgentSpecification;
    agentSpecifications?: Types.IAgentSpecification[];
    permissibleQueues?: TaskAgentQueue[];
    allQueues?: TaskAgentQueue[];
    allowInheritAgentQueue?: boolean;
    hideQueues?: boolean;
}

export class AgentsStore extends StoreCommonBase.ChangeTrackerStoreBase {

    constructor(args: IAgentsStoreArgs) {
        super();
        this._allowInheritAgentQueue = args.allowInheritAgentQueue;
        this._initializeState();
        this._originalAgentQueue = null;
        this._areAgentQueuesInitialized = false;
        this._areAgentSpecificationsInitialized = false;
        this._hideQueues = args.hideQueues;
        if (!!args.defaultQueueId) {
            this._originalAgentsState.defaultQueueId = args.defaultQueueId;
            this._agentsState.defaultQueueId = args.defaultQueueId;
        }
        if (args.permissibleQueues || args.allQueues) {
            this._initializeQueues({ permissibleQueues: args.permissibleQueues, allQueues: args.allQueues });
            this._originalAgentsState.defaultQueueId = this._agentsState.defaultQueueId;
        }
        this._setAgentSpecifications(args.agentSpecification, args.agentSpecifications);
    }

    public static getKey(): string {
        return StoreKeys.AgentsStore;
    }

    public initialize(instanceId?: string): void {
        super.initialize(instanceId);
        this._actionsHub = ActionsHubManager.GetActionsHub<AgentsActions>(AgentsActions, instanceId);
        this._actionsHub.createAgentsQueueSection.addListener(this._createAgentsQueueSection);
        this._actionsHub.updateAgentsQueueSection.addListener(this._updateAgentsQueueSection);
        this._actionsHub.updateAgentQueue.addListener(this._updateAgentQueue);
        this._actionsHub.updateAgentQueueWithSpecifications.addListener(this._updateAgentQueueWithSpecifications);
        this._actionsHub.refreshAgentQueue.addListener(this._refreshAgentQueue);
        this._actionsHub.manageAgent.addListener(this._manageAgentClicked);
        this._actionsHub.initializeAgentQueues.addListener(this._initializeAgentQueues);
        this._actionsHub.updateAgentSpecifications.addListener(this._handleUpdateAgentSpecifications);
        this._actionsHub.updateSelectedAgentSpecification.addListener(this._handleUpdateAgentSpecification);
        this._actionsHub.updateLoadingAgentSpecifications.addListener(this._updateLoadingAgentSpecifications);
    }

    protected disposeInternal(): void {
        this._actionsHub.createAgentsQueueSection.removeListener(this._createAgentsQueueSection);
        this._actionsHub.updateAgentsQueueSection.removeListener(this._updateAgentsQueueSection);
        this._actionsHub.updateAgentQueue.removeListener(this._updateAgentQueue);
        this._actionsHub.updateAgentQueueWithSpecifications.removeListener(this._updateAgentQueueWithSpecifications);
        this._actionsHub.refreshAgentQueue.removeListener(this._refreshAgentQueue);
        this._actionsHub.manageAgent.removeListener(this._manageAgentClicked);
        this._actionsHub.initializeAgentQueues.removeListener(this._initializeAgentQueues);
        this._actionsHub.updateAgentSpecifications.removeListener(this._handleUpdateAgentSpecifications);
        this._actionsHub.updateSelectedAgentSpecification.removeListener(this._handleUpdateAgentSpecification);
        this._actionsHub.updateLoadingAgentSpecifications.removeListener(this._updateLoadingAgentSpecifications);
    }

    public getTaskAgentQueues(): TaskAgentQueue[] {
        return this._agentsState.queues;
    }

    public isDirty(): boolean {
        const originalAgentSpecification = this._originalAgentsState.selectedAgentSpecification ? this._originalAgentsState.selectedAgentSpecification.identifier : null;
        const selectedAgentSpecification = this._agentsState.selectedAgentSpecification ? this._agentsState.selectedAgentSpecification.identifier : null;

        return (this._originalAgentsState.defaultQueueId !== this._agentsState.defaultQueueId)
            || (originalAgentSpecification !== selectedAgentSpecification);
    }

    public isValid(): boolean {
        let isValid: boolean;
        // If agent queues are not initialized, we return true since we can't validate. This is consistent with the old behavior
        if (!this._areAgentQueuesInitialized) {
            return true;
        }
        isValid = this._agentsState.queues.some(e => e.id === this._agentsState.defaultQueueId);

        isValid = isValid && (this._agentsState.agentSpecificationErrorMessage === "");
        return isValid;
    }

    public getState(): IAgentsState {
        return this._agentsState;
    }

    private _initializeState(): void {
        this._agentsState = this._getDefaultAgentState();
        this._originalAgentsState = this._getDefaultAgentState();
    }

    private _getDefaultAgentState(): IAgentsState {
        let queues: TaskAgentQueue[] = [];
        if (this._allowInheritAgentQueue) {
            queues.push(<TaskAgentQueue>{
                id: 0,
                name: Resources.InheritAgentQueueFromDefinition
            });
        }

        return <IAgentsState>{
            queues: queues,
            defaultQueueId: 0,
            selectedAgentSpecification: null,
            agentSpecificationErrorMessage: "",
            isLoadingAgentSpecifications: false,
            agentSpecifications: [],
        };
    }

    private _manageAgentClicked = () => {
        let actionUrl: string = TaskUtils.ActionUrlResolver.getActionUrl(null, null, "AgentQueue", { project: VssContext.getDefaultWebContext().project.name, area: "admin", queueId: this._agentsState.defaultQueueId, _a: "agents" });
        UrlUtilities.openInNewWindow(actionUrl, true);

        this.emitChanged();
    }

    private _updateAgentQueue = (queueId: number) => {
        // this implementation is deprecated (use _updateAgentQueueWithSpecifications instead)
        this._agentsState.defaultQueueId = queueId;
        this._agentsState.agentSpecifications = [];
        this._agentsState.selectedAgentSpecification = null;
        this._updateErrors();

        this.emitChanged();
    }

    private _updateAgentQueueWithSpecifications = (payload: IUpdateAgentQueuePayload) => {
        this._agentsState.defaultQueueId = payload.selectedQueueId;
        this._agentsState.agentSpecifications = (payload.agentSpecifications || []);
        this._agentsState.selectedAgentSpecification = payload.currentAgentSpecification;
        this._areAgentSpecificationsInitialized = true;
        this._updateErrors();
        this.emitChanged();
    }

    public getSelectedQueue(): TaskAgentQueue {
        let selectedQueue: TaskAgentQueue = Utils_Array.first(this._agentsState.queues, (queue: TaskAgentQueue) => {
            return queue.id === this._agentsState.defaultQueueId;
        });
        return selectedQueue;
    }

    private _createAgentsQueueSection = (payload: IActionQueuesPayload) => {
        this._createUpdateAgentsQueueSection(payload);
        this._updateErrors();
        this.emitChanged();
    }

    private _updateAgentsQueueSection = (payload: IActionQueuesPayload) => {
        const updateRequired = this._updateRequired(payload);
        const isDirtyBeforeUpdate = this.isDirty();
        this._createUpdateAgentsQueueSection(payload);

        // Due to performance rendering issues we can not update the UI after save in all cases,
        // hence emitChanged is not called after save definition if the payload does not differ from the current state.
        // However in create/revert definition the UI should always be updated.
        if (payload.forceUpdate || updateRequired || isDirtyBeforeUpdate !== this.isDirty()) {
            this._updateErrors();
            this.emitChanged();
        }
    }

    private _updateRequired(payload: IActionQueuesPayload): boolean {
        // check to see if all the queues in the current state are still present in the latest payload
        if (payload.queues && this._agentsState.queues) {
            const payloadQueues = payload.queues.filter(queue => !queue.pool || (queue.pool && !queue.pool.isLegacy));
            this._ensureQueue(payloadQueues, payload.agentQueueFromBuild);
            if (this._agentsState.queues.find(q => !payloadQueues.find(agentStateQueue => q.id === agentStateQueue.id))) {

                return true;
            }
        }

        return false;
    }

    private _createUpdateAgentsQueueSection(payload: IActionQueuesPayload): void {
        const processDefaultInvalidQueueId = 0;
        const phaseDefaultInvaliQueueId = -1;

        // Null handling for Queues here
        let agentQueues = payload.queues ? AgentUtils.createTaskAgentQueueCopy(payload.queues) : [];

        // filter out any queues that have a pool that is flagged as "legacy"
        // note that "legacy" can equate to visibility for migration purposes and is not necessarily
        // an indication that the pool is being deprecated
        agentQueues = agentQueues.filter(queue => !queue.pool || (queue.pool && !queue.pool.isLegacy));

        if (payload.agentQueueFromBuild && payload.agentQueueFromBuild.name) {
            // Filling up the original & current State default queue id
            this._originalAgentsState.defaultQueueId = payload.agentQueueFromBuild.id;
            this._agentsState.defaultQueueId = payload.agentQueueFromBuild.id;

            this._originalAgentQueue = AgentUtils.createBuildTypeAgentCopy(payload.agentQueueFromBuild);
            this._ensureQueue(agentQueues, payload.agentQueueFromBuild);
            this._setAgentSpecifications(payload.currentAgentSpecification || null, payload.agentSpecifications || []);
        }
        else {
            // unset the queue
            // For definition level we will use the default invalid QueueId to 0
            this._agentsState.defaultQueueId = processDefaultInvalidQueueId;
            this._setAgentSpecifications(undefined, []);
            if (this._allowInheritAgentQueue) {
                // If currentQueueId is set to 0, <inherit from definition>, we wont be able to find agentQueueFromBuild and we wont run if part.
                // In this case reset defaultQueueId = 0 for both states
                if (payload.currentQueueId === 0) {
                    this._agentsState.defaultQueueId = 0;
                    this._originalAgentsState.defaultQueueId = 0;
                }
                // for phase level, default invalid QueueId 0 is reserved for <inherit from definition> option so we will use -1.
                else {
                    this._agentsState.defaultQueueId = phaseDefaultInvaliQueueId;
                    this._originalAgentsState.defaultQueueId = phaseDefaultInvaliQueueId;
                }
            }
        }

        // Filling Data in queues
        this._setQueueList(agentQueues);
    }

    private _ensureQueue(queues: TaskAgentQueue[], existingQueue: TaskAgentQueue): void {
        // For the deleted queue case, defaultQueue will be null, so we won't insert it into the list of queues.
        if (existingQueue) {
            let configuredQueue = Utils_Array.first(queues, (queue: TaskAgentQueue) => {
                return queue.id === existingQueue.id;
            });

            // Just because the queue isn't returned in the list doesn't mean we shouldn't include it in the
            // drop-down if we have the information. This can occur because the queue configured on the definition
            // is not 'use'-able by the user making this call.
            if (!configuredQueue) {
                queues.push(existingQueue);
            }
        }
    }

    private _refreshAgentQueue = (refreshedQueues: TaskAgentQueue[]) => {
        refreshedQueues = refreshedQueues || [];
        // filter out any queues that have a pool that is flagged as "legacy"
        // note that "legacy" can equate to visibility for migration purposes and is not necessarily
        // an indication that the pool is being deprecated
        this._setAgentQueues(refreshedQueues.filter(queue => !queue.pool || (queue.pool && !queue.pool.isLegacy)));
        this._updateErrors();
        this.emitChanged();
    }

    private _initializeAgentQueues = (payload: IActionEditDefintionQueuesPayload) => {
        let queueId = this._initializeQueues(payload);
        this._updateErrors();

        // If the queueId has changed then only propogate the change
        // This is make sure we dont emit change in case nothing changes.
        if (queueId <= 0 || queueId !== this._agentsState.defaultQueueId) {
            this.emitChanged();
        }
    }

    private _initializeQueues = (payload: IActionEditDefintionQueuesPayload): number => {
        const queueId = this._agentsState.defaultQueueId;
        // filter out any queues that have a pool that is flagged as "legacy"
        // note that "legacy" can equate to visibility for migration purposes and is not necessarily
        // an indication that the pool is being deprecated
        const queues: TaskAgentQueue[] = payload.permissibleQueues.filter(queue => !queue.pool || (queue.pool && !queue.pool.isLegacy));

        // Check if default queue is present under queue list for which user has permission. If it is not there then get
        //  details from all queue list. We want to append default queue into the permissible queue list, this is to handle
        // case where some other user has created the definition and other user is opening the definition and not having permission
        // on the agent saved with the definition
        let defaultQueue = this._getQueueDetailsIfPresent(queueId, queues);
        if (!defaultQueue) {
            defaultQueue = this._getQueueDetailsIfPresent(queueId, payload.allQueues);
            if (defaultQueue) {
                queues.push(defaultQueue);
            }
        }

        this._setAgentQueues(queues);

        return queueId;
    }

    private _getQueueDetailsIfPresent(queueId: number, queues: TaskAgentQueue[]): TaskAgentQueue {
        let queueDetails: TaskAgentQueue = null;
        if (queues) {
            for (let queue of queues) {
                if (queue && queue.id === queueId) {
                    queueDetails = queue;
                    break;
                }
            }
        }

        return queueDetails;
    }

    private _setAgentQueues(queues: TaskAgentQueue[]) {
        const payloadQueues: TaskAgentQueue[] = queues ? AgentUtils.createTaskAgentQueueCopy(queues) : [];

        // If this._originalAgentQueue is null and this._agentsState.defaultQueueId is set, it means, we are getting the queueList for the first time
        if (this._originalAgentQueue === null && this._agentsState.defaultQueueId > 0) {
            this._originalAgentQueue = payloadQueues.filter(e => e.id === this._agentsState.defaultQueueId)[0];
        }

        if (!!this._originalAgentQueue) {
            this._agentsState.defaultQueueId = this._originalAgentQueue.id;
            this._agentsState.selectedAgentSpecification = this._originalAgentsState.selectedAgentSpecification;
            this._agentsState.agentSpecifications = this._originalAgentsState.agentSpecifications;
        }
        else {
            // set an invalid id
            this._agentsState.defaultQueueId = 0;
            this._setAgentSpecifications(undefined, []);
        }

        this._ensureQueue(payloadQueues, this._originalAgentQueue);
        this._setQueueList(payloadQueues);
    }

    private _setQueueList(queues: TaskAgentQueue[]): void {
        if (this._allowInheritAgentQueue) {
            const inheritOption = <TaskAgentQueue>{
                id: 0,
                name: Resources.InheritAgentQueueFromDefinition
            };

            if (this._hideQueues) {
                queues = [inheritOption];
            }
            else {
                queues.push(inheritOption);
            }
        }

        // Make sure the queues are consistently sorted for UI display
        queues.sort((a: TaskAgentQueue, b: TaskAgentQueue) => {
            return Utils_String.localeIgnoreCaseComparer(a.name, b.name);
        });

        // Set the queue list
        this._agentsState.queues = queues;
        this._areAgentQueuesInitialized = true;
    }

    private _handleUpdateAgentSpecifications = (agentSpecifications: Types.IAgentSpecification[]) => {
        this._areAgentSpecificationsInitialized = true;
        if (!agentSpecifications) {
            agentSpecifications = [];
        }

        this._agentsState.agentSpecifications = [...agentSpecifications];

        // clear the selected agent specification if there are no agent specifications
        if (!this._agentsState.agentSpecifications || this._agentsState.agentSpecifications.length === 0) {
            this._agentsState.selectedAgentSpecification = null;
        }

        this._updateErrors();

        this.emit(AgentsStoreChangedEvents.AgentSpecificationsChangedEvent, this);
    }

    private _updateLoadingAgentSpecifications = (loading: boolean) => {
        this._agentsState.isLoadingAgentSpecifications = loading;
        this.emitChanged();
    }

    private _handleUpdateAgentSpecification = (agentSpecification: Types.IAgentSpecification) => {
        this._agentsState.selectedAgentSpecification = agentSpecification;
        this._updateErrors();
        this.emitChanged();
    }

    private _setAgentSpecifications = (agentSpecification: Types.IAgentSpecification, agentSpecifications: Types.IAgentSpecification[]) => {
        this._originalAgentsState.selectedAgentSpecification = agentSpecification;
        this._agentsState.selectedAgentSpecification = agentSpecification;
        this._originalAgentsState.agentSpecifications = agentSpecifications;
        this._agentsState.agentSpecifications = agentSpecifications;
        if (agentSpecifications && agentSpecifications.length) {
            this._areAgentSpecificationsInitialized = true;
        }
    }

    private _updateErrors(): void {

        // make sure a valid agent spec is selected if one is selected
        if (this._agentsState.selectedAgentSpecification && this._areAgentSpecificationsInitialized) {
            if (!this._agentsState.agentSpecifications
                || !this._agentsState.agentSpecifications.some(a => a.identifier === this._agentsState.selectedAgentSpecification.identifier)) {
                this._agentsState.agentSpecificationErrorMessage = Resources.InvalidAgentSpecification;
                return;
            }
        }
        // validate an agent spec is selected if there are agent specs available
        if (!this._agentsState.selectedAgentSpecification
            && this._agentsState.agentSpecifications
            && this._agentsState.agentSpecifications.length > 0)  {
            this._agentsState.agentSpecificationErrorMessage = Resources.AgentSpecificationRequired;
            return;
        }

        this._agentsState.agentSpecificationErrorMessage = "";
    }

    private _areAgentQueuesInitialized: boolean;
    private _areAgentSpecificationsInitialized: boolean;
    private _agentsState: IAgentsState;
    private _originalAgentQueue: TaskAgentQueue;
    private _originalAgentsState: IAgentsState;
    private _actionsHub: AgentsActions;
    private _allowInheritAgentQueue: boolean;
    private _hideQueues: boolean;
}

export class AgentUtils {

    /**
     * @brief Creates a copy of agentQueue instance object
     * @param agentInstance
     */
    public static createBuildTypeAgentCopy(agentInstance: TaskAgentQueue): TaskAgentQueue {
        let agentQueue: TaskAgentQueue = <TaskAgentQueue>{
            id: agentInstance.id,
            name: agentInstance.name
        };
        if (agentInstance.pool) {
            agentQueue.pool = <TaskAgentPoolReference>{
                id: agentInstance.pool.id,
                name: agentInstance.pool.name,
                isHosted: agentInstance.pool.isHosted
            };
        }
        return agentQueue;
    }

    public static createTaskAgentQueueCopy(agents: TaskAgentQueue[]): TaskAgentQueue[] {
        let agentCopy: TaskAgentQueue[] = [];
        agents.forEach((agent: TaskAgentQueue) => {
            let agentQueue: TaskAgentQueue = <TaskAgentQueue>{
                id: agent.id,
                name: agent.name,
                projectId: agent.projectId
            };

            if (agent.pool) {
                agentQueue.pool = {
                    id: agent.pool.id,
                    isHosted: agent.pool.isHosted,
                    name: agent.pool.name,
                    poolType: agent.pool.poolType,
                    scope: agent.pool.scope,
                    size: agent.pool.size,
                    isLegacy: agent.pool.isLegacy,
                    options: agent.pool.options
                };
            }
            agentCopy.push(agentQueue);
        });
        return agentCopy;
    }
}